import { useState, useEffect, useCallback } from 'react';

const listeners = new Map<string, Set<(value: any) => void>>();

export function useLocalStorage<T>(key: string, defaultValue: T) {
  const readValue = () => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const [value, setValueState] = useState<T>(readValue);

  const setValue = useCallback(
    (val: T | ((prev: T) => T)) => {
      setValueState((prev) => {
        const newVal =
          typeof val === 'function' ? (val as (prev: T) => T)(prev) : val;
        try {
          localStorage.setItem(key, JSON.stringify(newVal));
        } catch (err) {
          console.warn('Failed to write to localStorage', err);
        }
        queueMicrotask(() => {
          listeners.get(key)?.forEach((fn) => fn(newVal));
        });

        return newVal;
      });
    },
    [key],
  );

  useEffect(() => {
    const updateListeners = listeners.get(key) || new Set();
    updateListeners.add(setValueState);
    listeners.set(key, updateListeners);

    return () => {
      updateListeners.delete(setValueState);
      if (updateListeners.size === 0) {
        listeners.delete(key);
      }
    };
  }, [key]);

  return [value, setValue] as const;
}
